/* eslint-disable react-hooks/set-state-in-effect, no-empty */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    Users, FileText, Target, Star, BarChart2, Download,
    Activity, Loader2, FileDown
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import KpiTooltip from '../../components/shared/KpiTooltip';

// Sub-components (refactored)
import AgencyKPIs from '../../components/agency/AgencyKPIs';
import MonthlyComparison from '../../components/agency/MonthlyComparison';
import LeadSourcesKPI from '../../components/agency/LeadSourcesKPI';
import SatisfactionWidget from '../../components/agency/SatisfactionWidget';
import AgencyCharts from '../../components/agency/AgencyCharts';
import AgentRankingList from '../../components/agency/AgentRankingList';
import RecognitionSuggestions from '../../components/engagement/RecognitionSuggestions';
import AgencyQuickActions from '../../components/agency/AgencyQuickActions';
import KPIBouquetsStrip from '../../components/cockpit/KPIBouquetsStrip';
import WeeklyRecapBanner from '../../components/cockpit/WeeklyRecapBanner';

export default function AgencyDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState([]);
    const [stats, setStats] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('mandates');
    const [period, setPeriod] = useState('month');
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [chartData, setChartData] = useState(null);
    const [activeTab, setActiveTab] = useState('ranking');
    const [reportMenuOpen, setReportMenuOpen] = useState(false);
    const [satisfaction, setSatisfaction] = useState(null);
    const [generatingReport, setGeneratingReport] = useState(false);

    // --- Actions ---
    const handleExport = async (type) => {
        setExporting(true);
        setExportMenuOpen(false);
        try {
            const endpoints = {
                agents: '/export/agents/csv',
                mandates: '/export/mandates/csv',
                offers: '/export/offers/csv',
                activity: `/export/activity/csv?period=${period}`
            };
            const response = await api.get(endpoints[type], { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Export réussi !');
        } catch {
            toast.error("Erreur lors de l'export");
        } finally {
            setExporting(false);
        }
    };

    const handleGenerateReport = async () => {
        setGeneratingReport(true);
        setReportMenuOpen(false);
        try {
            const response = await api.get('/admin/agency/export/monthly-pdf?months=6', { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Homly_Bilan_Mensuel_${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Bilan PDF généré avec succès !');
        } catch {
            toast.error('Erreur lors de la génération du bilan');
        } finally {
            setGeneratingReport(false);
        }
    };

    // --- Data loading ---
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const agentsRes = await api.get('/admin/agency/agents');
            setAgents(agentsRes.data.agents || []);
            const statsRes = await api.get(`/admin/agency/stats?period=${period}`);
            setStats(statsRes.data);
            try {
                const chartsRes = await api.get('/admin/agency/performance-charts?months=3');
                setChartData(chartsRes.data);
            } catch {}
            try {
                const satRes = await api.get('/admin/agency/satisfaction');
                setSatisfaction(satRes.data);
            } catch {}
        } catch {
            setAgents([]);
            setStats({ total_agents: 0, total_mandates: 0, closings_this_month: 0, avg_rating: 0 });
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => { loadData(); }, [loadData]);

    // --- Loading state ---
    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shadow-xl">
                    <BarChart2 className="w-7 h-7 text-white" />
                </div>
                <div className="text-center">
                    <p className="text-white font-semibold">Chargement des KPIs...</p>
                    <p className="text-xs text-muted-foreground mt-1">Analyse des performances en cours</p>
                </div>
                <Loader2 className="w-6 h-6 text-[#FF914D] animate-spin" />
            </div>
        );
    }

    // --- Stat cards config ---
    const statCards = [
        { icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/20', value: stats?.total_agents, label: 'Agents', tip: "Nombre total d'agents actifs dans votre agence." },
        { icon: FileText, color: 'text-purple-400', bg: 'bg-purple-500/20', value: stats?.total_mandates, label: 'Mandats', tip: "Nombre total de mandats actifs (en cours de vente) sur la période sélectionnée." },
        { icon: Target, color: 'text-green-400', bg: 'bg-green-500/20', value: stats?.closings_this_month, label: 'Ventes', tip: "Nombre de mandats passés au statut \"vendu\" ce mois-ci. Un closing = une vente signée." },
        { icon: Star, color: 'text-yellow-400', bg: 'bg-yellow-500/20', value: stats?.avg_rating?.toFixed(1), label: 'Note moy.', tip: "Note moyenne attribuée par les vendeurs à vos agents (sur 5). Basée sur les évaluations reçues." },
    ];

    return (
        <div className="min-h-screen bg-[#0a0a0f] p-3 sm:p-6" data-testid="agency-dashboard">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-8">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <h1 className="text-xl sm:text-3xl font-bold text-white truncate">KPIs Agence</h1>
                            <p className="text-xs sm:text-sm text-muted-foreground">Performances de vos agents</p>
                        </div>
                        {/* Mobile actions */}
                        <div className="flex items-center gap-1.5 sm:hidden">
                            <Button onClick={handleGenerateReport} disabled={generatingReport} size="icon" variant="outline" className="w-8 h-8 border-border text-muted-foreground" data-testid="mobile-report-button">
                                {generatingReport ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                            </Button>
                            <div className="relative">
                                <Button onClick={() => setExportMenuOpen(!exportMenuOpen)} size="icon" variant="outline" className="w-8 h-8 border-border text-muted-foreground" data-testid="mobile-export-button">
                                    <Download className="w-3.5 h-3.5" />
                                </Button>
                                {exportMenuOpen && (
                                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="absolute top-full right-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                                        {[
                                            { type: 'agents', icon: Users, color: 'text-blue-400', label: 'Agents' },
                                            { type: 'mandates', icon: FileText, color: 'text-purple-400', label: 'Mandats' },
                                            { type: 'offers', icon: '€', color: 'text-green-400', label: 'Offres' },
                                        ].map(item => (
                                            <button key={item.type} onClick={() => handleExport(item.type)} className="w-full px-3 py-2.5 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2">
                                                {typeof item.icon === 'string' ? <span className={item.color}>{item.icon}</span> : <item.icon className={`w-3.5 h-3.5 ${item.color}`} />} {item.label}
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Period selector */}
                    <div className="flex gap-1.5 sm:gap-2 overflow-x-auto scrollbar-hide" data-testid="period-selector">
                        {[
                            { value: 'week', label: 'Sem.' },
                            { value: 'month', label: 'Mois' },
                            { value: 'quarter', label: 'Trim.' },
                            { value: 'year', label: 'Année' }
                        ].map(p => (
                            <Button
                                key={p.value}
                                onClick={() => setPeriod(p.value)}
                                size="sm"
                                className={`text-xs shrink-0 rounded-full h-7 px-3 sm:h-8 sm:px-4 ${period === p.value
                                    ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg'
                                    : 'bg-card text-muted-foreground hover:text-white border border-border'}`}
                                data-testid={`period-${p.value}`}
                            >
                                {p.label}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Stats Grid */}
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
                        {statCards.map((stat, idx) => (
                            <Card key={idx} className="bg-slate-900 border-border">
                                <CardContent className="p-3 sm:p-4">
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className={`p-1.5 sm:p-2 rounded-lg ${stat.bg}`}>
                                            <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${stat.color}`} />
                                        </div>
                                        <div>
                                            <p className="text-lg sm:text-2xl font-bold text-white">{stat.value}</p>
                                            <p className="text-[10px] sm:text-xs text-muted-foreground">{stat.label}<KpiTooltip text={stat.tip} /></p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Encart hebdomadaire chiffré — P5 */}
                <WeeklyRecapBanner scope="agency" />

                {/* Vue synthétique — 5 bouquets KPI agence (P3) */}
                <div className="mb-4 sm:mb-6" data-testid="agency-bouquets-strip">
                    <KPIBouquetsStrip scope="agency" variant="admin" />
                </div>

                {/* Satisfaction */}
                <SatisfactionWidget satisfaction={satisfaction} />

                {/* Detailed KPIs */}
                <AgencyKPIs />
                <MonthlyComparison />
                <LeadSourcesKPI />

                {/* Tab Navigation */}
                <div className="flex gap-1.5 sm:gap-2 mb-4 sm:mb-6">
                    {[
                        { key: 'ranking', icon: Users, label: 'Classement' },
                        { key: 'charts', icon: Activity, label: 'Graphiques' }
                    ].map(tab => (
                        <Button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            size="sm"
                            className={`text-xs rounded-full h-7 px-3 sm:h-8 sm:px-4 ${activeTab === tab.key
                                ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white shadow-lg'
                                : 'bg-card text-muted-foreground hover:text-white border border-border'}`}
                        >
                            <tab.icon className="w-3.5 h-3.5 mr-1.5" />
                            {tab.label}
                        </Button>
                    ))}
                </div>

                {/* Charts */}
                {activeTab === 'charts' && <AgencyCharts chartData={chartData} />}

                {/* Agent Ranking */}
                {activeTab === 'ranking' && (
                    <AgentRankingList
                        agents={agents}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                    />
                )}

                {/* M5 — Reconnaissance Manager → Agent (suggestions Homly) */}
                <RecognitionSuggestions />

                {/* Quick Actions */}
                <AgencyQuickActions
                    navigate={navigate}
                    handleGenerateReport={handleGenerateReport}
                    handleExport={handleExport}
                    generatingReport={generatingReport}
                    exporting={exporting}
                    reportMenuOpen={reportMenuOpen}
                    setReportMenuOpen={setReportMenuOpen}
                    exportMenuOpen={exportMenuOpen}
                    setExportMenuOpen={setExportMenuOpen}
                />
            </div>
        </div>
    );
}
